import React, { useState, useRef } from 'react';
import {
  Typography,
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  CircularProgress,
  Stepper,
  Step,
  StepLabel,
  Chip,
  Autocomplete,
} from '@mui/material';
import {
  Done,
  Add,
  LocationOn,
  CheckCircle,
  Delete,
  Edit,
} from '@mui/icons-material';
import { useBundleCreation } from '../../contexts/BundleCreationContext';
import { useCreateBundle, useDeductChildSKUs, useAddBundleToInventory } from '../../hooks/useWarehouseData';
import { WarehouseAPI } from '../../services/api';
import { getAggregatedChildSKUs } from '../../utils/inventoryUtils';
import { newOperationId } from '../../utils/operationId';

interface BundleCreatorProps {
  onComplete: () => void;
}

const MultipleStorageLocationSelector: React.FC = () => {
  const { state, dispatch } = useBundleCreation();
  const [newLocation, setNewLocation] = useState('');
  const [newQuantity, setNewQuantity] = useState(1);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [clearKey, setClearKey] = useState(0); // Force re-render when clearing

  // Generate shelf options
  const generateShelfOptions = () => {
    const options = [
      'B2B-BLINKIT', 'B2B-CONSUMPTION', 'B2B-FBA', 'B2B-KLYDO', 'B2B-KNOT', 'B2B-MYNTRA',
      'B2B-SLIKK', 'B2B-ZEPTO', 'B2B-ZILO', 'BLINKIT', 'BLINKIT-NC', 'blocked_bin',
      'CARDING', 'CARDING-CONSUMPTION', 'D2C-CONSUMPTION', 'D2C-GB', 'DEFAULT',
      'LOVEBOX-INSERT', 'MNEW', 'NCNEW', 'NECKLACE-CARD', 'NON-CARDED',
      'NONCARDED-1', 'NONCARDED-2', 'NONCARDED-3', 'NONCARDED-4', 'NONCARDED-5',
      'NONCARDED-6', 'NONCARDED-7', 'NONCARDED-8', 'NONCARDED-9', 'NONCARDED-10',
      'NONCARDED-11', 'NONCARDED-12', 'NONCARDED-13', 'NONCARDED-14', 'NONCARDED-15',
      'NONCARDED-16', 'NONCARDED-17', 'NONCARDED-18', 'NONCARDED-19', 'NONCARDED-20',
      'NOT_FOUND',
      'PACKAGING', 'RAKHI', 'TEST', 'TEST2', 'WH-PHOTOSHOOT', 'SHOO5'
    ];

    // Add PA series
    for (let i = 1; i <= 10; i++) options.push(`PA${i}`);
    // Add PB series
    for (let i = 1; i <= 10; i++) options.push(`PB${i}`);
    // Add PC series
    for (let i = 1; i <= 12; i++) options.push(`PC${i}`);
    // Add PR series
    options.push('PR1', 'PR2', 'pr3', 'pr4', 'pr5', 'pr6', 'pr7');
    // Add RAKHI series
    for (let i = 1; i <= 9; i++) options.push(`RAKHI-${i}`);
    // Add VS series
    for (let i = 1; i <= 100; i++) options.push(`VS${i.toString().padStart(3, '0')}`);
    // Add GF series
    for (let i = 1; i <= 15; i++) options.push(`GF${i.toString().padStart(3, '0')}`);
    // Add GF MIX_RTO series
    for (let i = 1; i <= 5; i++) options.push(`GF${i.toString().padStart(3, '0')}-MIX_RTO`);
    // Add SH series
    for (let i = 1; i <= 220; i++) {
      const num = i.toString().padStart(3, '0');
      if (i === 102) options.push('sh102');
      else if (i === 113) options.push('sh113');
      else if (i === 114) options.push('sh114');
      else if (i === 115) options.push('sh115');
      else if (i === 116) options.push('Sh116');
      else options.push(`SH${num}`);
    }

    return Array.from(new Set(options)).sort();
  };

  const shelfOptions = generateShelfOptions();
  const totalBundleQuantity = state.quantity;
  const allocatedQuantity = state.bundleStorageLocations.reduce((sum, loc) => sum + loc.quantity, 0);
  const remainingQuantity = totalBundleQuantity - allocatedQuantity;

  const handleAddLocation = () => {
    if (!newLocation.trim() || newQuantity <= 0 || newQuantity > remainingQuantity) {
      return;
    }

    if (editingIndex !== null) {
      // Update existing location
      dispatch({
        type: 'UPDATE_BUNDLE_STORAGE_LOCATION',
        payload: { shelfLocation: newLocation, quantity: newQuantity }
      });
      setEditingIndex(null);
    } else {
      // Add new location
      dispatch({
        type: 'ADD_BUNDLE_STORAGE_LOCATION',
        payload: { shelfLocation: newLocation, quantity: newQuantity }
      });
    }

    setNewLocation('');
    setNewQuantity(1);
    setClearKey(prev => prev + 1); // Force Autocomplete to re-render and clear
  };

  const handleEditLocation = (index: number) => {
    const location = state.bundleStorageLocations[index];
    setNewLocation(location.shelfLocation);
    setNewQuantity(location.quantity);
    setEditingIndex(index);
  };

  const handleRemoveLocation = (shelfLocation: string) => {
    dispatch({
      type: 'REMOVE_BUNDLE_STORAGE_LOCATION',
      payload: shelfLocation
    });
  };

  const handleCancelEdit = () => {
    setEditingIndex(null);
    setNewLocation('');
    setNewQuantity(1);
    setClearKey(prev => prev + 1); // Force Autocomplete to re-render and clear
  };

  const isFormValid = newLocation.trim() && newQuantity > 0 && newQuantity <= remainingQuantity + (editingIndex !== null ? state.bundleStorageLocations[editingIndex]?.quantity || 0 : 0);

  return (
    <Card sx={{ mb: 3 }}>
      <CardContent>
        <Typography variant="subtitle1" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <LocationOn />
          Bundle Storage Locations (Multiple Locations)
        </Typography>

        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Distribute {totalBundleQuantity} bundle{totalBundleQuantity > 1 ? 's' : ''} across multiple storage locations.
          Remaining to allocate: <strong>{remainingQuantity}</strong>
        </Typography>

        {/* Add/Edit Location Form */}
        <Box sx={{ mb: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
          <Box sx={{
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            gap: 2,
            alignItems: 'end'
          }}>
            <Box sx={{ flex: { xs: 1, md: 2 } }}>
              <Autocomplete
                key={clearKey} // Force re-render to clear input
                options={shelfOptions}
                value={newLocation || null}
                onChange={(event, newValue) => setNewLocation(newValue || '')}
                filterOptions={(options, { inputValue }) =>
                  options.filter((option) =>
                    option.toLowerCase().includes(inputValue.toLowerCase())
                  )
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Storage Location"
                    placeholder="Type to search shelves"
                    fullWidth
                  />
                )}
                freeSolo
              />
            </Box>
            <Box sx={{ flex: 1 }}>
              <TextField
                type="number"
                label="Quantity"
                value={newQuantity}
                onChange={(e) => setNewQuantity(Math.max(1, parseInt(e.target.value) || 1))}
                inputProps={{
                  min: 1,
                  max: remainingQuantity + (editingIndex !== null ? state.bundleStorageLocations[editingIndex]?.quantity || 0 : 0)
                }}
                fullWidth
              />
            </Box>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                variant="contained"
                onClick={handleAddLocation}
                disabled={!isFormValid}
                startIcon={editingIndex !== null ? <Edit /> : <Add />}
                size="small"
              >
                {editingIndex !== null ? 'Update' : 'Add'}
              </Button>
              {editingIndex !== null && (
                <Button
                  variant="outlined"
                  onClick={handleCancelEdit}
                  size="small"
                >
                  Cancel
                </Button>
              )}
            </Box>
          </Box>
        </Box>

        {/* Location List */}
        {state.bundleStorageLocations.length > 0 && (
          <List>
            {state.bundleStorageLocations.map((location, index) => (
              <ListItem key={`${location.shelfLocation}-${index}`} divider>
                <ListItemText
                  primary={location.shelfLocation}
                  secondary={`Quantity: ${location.quantity}`}
                />
                <ListItemSecondaryAction>
                  <IconButton
                    edge="end"
                    onClick={() => handleEditLocation(index)}
                    sx={{ mr: 1 }}
                    size="small"
                  >
                    <Edit />
                  </IconButton>
                  <IconButton
                    edge="end"
                    onClick={() => handleRemoveLocation(location.shelfLocation)}
                    color="error"
                    size="small"
                  >
                    <Delete />
                  </IconButton>
                </ListItemSecondaryAction>
              </ListItem>
            ))}
          </List>
        )}

        {/* Summary */}
        {allocatedQuantity > 0 && (
          <Alert severity={remainingQuantity === 0 ? "success" : "info"} sx={{ mt: 2 }}>
            Allocated: {allocatedQuantity} / {totalBundleQuantity} bundles
            {remainingQuantity > 0 && ` (${remainingQuantity} remaining)`}
          </Alert>
        )}
      </CardContent>
    </Card>
  );
};

const BundleCreator: React.FC<BundleCreatorProps> = ({ onComplete }) => {
  const { state, dispatch, saveDraft } = useBundleCreation();
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [currentOperation, setCurrentOperation] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Synchronous re-entrancy lock. `currentOperation` state can't do this job: it only
  // takes effect on the next render, so a second invocation in the same tick (double
  // click, Enter + click) would still see it unset and run the whole creation again.
  const creationInFlightRef = useRef(false);
  // One id per logical creation, kept across an error -> Retry so the backend recognises
  // the retry as the same adjustment (and never books the stock twice). Cleared on success.
  const creationIdRef = useRef<string | null>(null);
  // Same idea for "Put Back Child": one id per put-back, reused if it is retried after a
  // partial failure, so lines that already went back are replayed instead of added twice.
  const putBackIdRef = useRef<string | null>(null);
  const putBackInFlightRef = useRef(false);

  const deductMutation = useDeductChildSKUs();
  const createBundleMutation = useCreateBundle();
  const addBundleMutation = useAddBundleToInventory();

  const [puttingBack, setPuttingBack] = useState(false);
  const [reversalSuccess, setReversalSuccess] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);

  // Generate shelf options
  const generateShelfOptions = () => {
    const options = [
      'B2B-BLINKIT', 'B2B-CONSUMPTION', 'B2B-FBA', 'B2B-KLYDO', 'B2B-KNOT', 'B2B-MYNTRA',
      'B2B-SLIKK', 'B2B-ZEPTO', 'B2B-ZILO', 'BLINKIT', 'BLINKIT-NC', 'blocked_bin',
      'CARDING', 'CARDING-CONSUMPTION', 'D2C-CONSUMPTION', 'D2C-GB', 'DEFAULT',
      'LOVEBOX-INSERT', 'MNEW', 'NCNEW', 'NECKLACE-CARD', 'NON-CARDED',
      'NONCARDED-1', 'NONCARDED-2', 'NONCARDED-3', 'NONCARDED-4', 'NONCARDED-5',
      'NONCARDED-6', 'NONCARDED-7', 'NONCARDED-8', 'NONCARDED-9', 'NONCARDED-10',
      'NONCARDED-11', 'NONCARDED-12', 'NONCARDED-13', 'NONCARDED-14', 'NONCARDED-15',
      'NONCARDED-16', 'NONCARDED-17', 'NONCARDED-18', 'NONCARDED-19', 'NONCARDED-20',
      'NOT_FOUND',
      'PACKAGING', 'RAKHI', 'TEST', 'TEST2', 'WH-PHOTOSHOOT', 'SHOO5'
    ];

    // Add PA series
    for (let i = 1; i <= 10; i++) options.push(`PA${i}`);
    // Add PB series
    for (let i = 1; i <= 10; i++) options.push(`PB${i}`);
    // Add PC series
    for (let i = 1; i <= 12; i++) options.push(`PC${i}`);
    // Add PR series
    options.push('PR1', 'PR2', 'pr3', 'pr4', 'pr5', 'pr6', 'pr7');
    // Add RAKHI series
    for (let i = 1; i <= 9; i++) options.push(`RAKHI-${i}`);
    // Add VS series
    for (let i = 1; i <= 100; i++) options.push(`VS${i.toString().padStart(3, '0')}`);
    // Add GF series
    for (let i = 1; i <= 15; i++) options.push(`GF${i.toString().padStart(3, '0')}`);
    // Add GF MIX_RTO series
    for (let i = 1; i <= 5; i++) options.push(`GF${i.toString().padStart(3, '0')}-MIX_RTO`);
    // Add SH series
    for (let i = 1; i <= 220; i++) {
      const num = i.toString().padStart(3, '0');
      if (i === 102) options.push('sh102');
      else if (i === 113) options.push('sh113');
      else if (i === 114) options.push('sh114');
      else if (i === 115) options.push('sh115');
      else if (i === 116) options.push('Sh116');
      else options.push(`SH${num}`);
    }

    return Array.from(new Set(options)).sort();
  };

  const shelfOptions = generateShelfOptions();

  if (!state.selectedBundleSKU || !state.validationResult?.isValid) {
    return (
      <Alert severity="warning">
        Please complete all previous steps first.
      </Alert>
    );
  }

  const allChildSKUsPicked = getAggregatedChildSKUs(state.selectedBundleSKU.childSKUs).every((childSKU: any) => {
    const picking = state.childSKUPicking[childSKU.skuId];
    return picking && picking.totalPicked >= picking.totalRequired;
  });

  const handleBundleShelfLocationChange = (event: any, newValue: string | null) => {
    dispatch({ type: 'SET_BUNDLE_SHELF_LOCATION', payload: newValue || '' });
  };

  const totalAllocated = state.bundleStorageLocations.reduce((sum, loc) => sum + loc.quantity, 0);
  const hasValidStorageAllocation = state.bundleStorageLocations.length > 0
    ? totalAllocated === state.quantity
    : state.bundleShelfLocation.trim();

  const handleCreateBundle = async () => {
    if (!allChildSKUsPicked || !hasValidStorageAllocation) {
      return;
    }
    if (creationInFlightRef.current) return;
    creationInFlightRef.current = true;

    if (!creationIdRef.current) {
      creationIdRef.current = newOperationId();
    }
    const creationId = creationIdRef.current;

    try {
      setError(null);
      // Step 1: Deduct child SKUs
      setCurrentOperation('deduct');
      const shelfDeductions: Array<{ skuId: string, shelfLocation: string, quantity: number }> = [];
      Object.entries(state.childSKUPicking).forEach(([skuId, picking]) => {
        if (picking.picked && picking.shelfPickings.length > 0) {
          picking.shelfPickings.forEach(shelfPicking => {
            if (shelfPicking.quantityToPick > 0) {
              shelfDeductions.push({
                skuId,
                shelfLocation: shelfPicking.shelfLocation,
                quantity: shelfPicking.quantityToPick
              });
            }
          });
        }
      });

      // Convert to old format for API compatibility
      const shelfLocations: Record<string, string> = {};
      Object.entries(state.childSKUPicking).forEach(([skuId, picking]) => {
        if (picking.picked && picking.shelfPickings.length > 0) {
          // Use the first shelf location for backward compatibility
          shelfLocations[skuId] = picking.shelfPickings[0].shelfLocation;
        }
      });

      // Skip the old deduct method since we've already adjusted child SKU inventory on Unicommerce
      // Instead, we'll adjust the bundle SKU inventory on Unicommerce in the final step

      // NOTE: child SKU deduction logging should happen when inventory is updated on Unicommerce
      // (not immediately at bundle creation). We'll perform logging after the Unicommerce update step below.

      // Step 2: Create bundle physically (simulated)
      setCurrentOperation('create');
      await new Promise(resolve => setTimeout(resolve, 2000)); // Simulate bundle creation time

      // Step 3: Add bundle to Unicommerce inventory (multiple locations or single)
      setCurrentOperation('complete');
      if (state.bundleStorageLocations.length > 0) {
        // Handle multiple storage locations
        await Promise.all(
          state.bundleStorageLocations.map((location, idx) =>
            WarehouseAPI.adjustBundleInventoryOnUnicommerce(
              state.selectedBundleSKU!.id,
              location.quantity, // Positive quantity to ADD bundle to inventory
              location.shelfLocation,
              `${creationId}:${idx}:${location.shelfLocation}:${location.quantity}`
            )
          )
        );
      } else {
        // Handle single location (backward compatibility)
        await WarehouseAPI.adjustBundleInventoryOnUnicommerce(
          state.selectedBundleSKU!.id,
          state.quantity, // Positive quantity to ADD bundle to inventory
          state.bundleShelfLocation,
          `${creationId}:0:${state.bundleShelfLocation}:${state.quantity}`
        );
      }

      // Child SKU deductions are logged when inventory is synced from the
      // picking step (ChildSKUPicker). Avoid duplicating those logs here.

      // Log bundle creation to backend
      try {
        const sBundle = state.selectedBundleSKU!;
        const childSkusLogged = getAggregatedChildSKUs(sBundle.childSKUs).map((c: any) => ({ sku: c.skuId, qty: c.requiredQuantity * state.quantity }));

        if (state.bundleStorageLocations.length > 0) {
          // Log each storage location separately
          await Promise.all(
            state.bundleStorageLocations.map(location =>
              WarehouseAPI.logBundleCreation({
                bundleSku: sBundle.id,
                quantity: location.quantity,
                childSkus: childSkusLogged.map(c => ({ sku: c.sku, qty: Math.round((c.qty * location.quantity) / state.quantity) })),
                shelfLocation: location.shelfLocation,
                metadata: {
                  totalBundleQuantity: state.quantity,
                  storageLocations: state.bundleStorageLocations
                }
              })
            )
          );
        } else {
          // Single location logging (backward compatibility)
          await WarehouseAPI.logBundleCreation({
            bundleSku: sBundle.id,
            quantity: state.quantity,
            childSkus: childSkusLogged,
            shelfLocation: state.bundleShelfLocation
          });
        }
      } catch (logErr) {
        console.warn('Bundle creation logging failed', logErr);
      }

      // If this was a draft, delete it from the backend now that creation is complete
      if (state.isDraft && state.draftId) {
        try {
          await WarehouseAPI.deleteDraft(state.draftId);
        } catch (delErr) {
          console.warn('Failed to delete draft after creation', delErr);
        }
      }

      setShowConfirmDialog(false);
      setCurrentOperation(null);
      creationIdRef.current = null;

      // Show success modal
      setShowSuccessModal(true);

    } catch (error: any) {
      console.error('Bundle creation failed:', error);
      setError(error.message || 'Bundle creation failed');
      setCurrentOperation(null);
    } finally {
      creationInFlightRef.current = false;
    }
  };

  const handlePutBackChild = async () => {
    if (puttingBack || putBackInFlightRef.current) return;
    putBackInFlightRef.current = true;
    if (!putBackIdRef.current) putBackIdRef.current = newOperationId();
    const putBackId = putBackIdRef.current;
    setPuttingBack(true);
    setError(null);
    try {
      // Step: Prepare reversal adjustments (add quantities back)
      const reversalAdjustments: any[] = [];
      Object.entries(state.childSKUPicking).forEach(([skuId, picking]) => {
        if (picking.picked && picking.shelfPickings.length > 0) {
          picking.shelfPickings.forEach(shelfPicking => {
            if (shelfPicking.quantityToPick > 0) {
              reversalAdjustments.push({
                skuCode: skuId,
                shelfCode: shelfPicking.shelfLocation,
                quantity: shelfPicking.quantityToPick, // Positive to ADD back
                inventoryType: 'GOOD_INVENTORY',
                operation: 'ADD'
              });
            }
          });
        }
      });

      if (reversalAdjustments.length > 0) {
        // Use addChildSKUsToInventory which is proven to work for adding items back (used in debundling)
        const putBackResult = await WarehouseAPI.addChildSKUsToInventory(reversalAdjustments.map(a => ({
          skuCode: a.skuCode,
          shelfCode: a.shelfCode,
          quantity: a.quantity
        })), putBackId);
        // addChildSKUsToInventory reports partial failure via its result instead of
        // throwing — don't treat that as a completed reversal. Retrying is safe: lines
        // that already went back are replayed, not added again.
        if (!putBackResult.success) {
          throw new Error(putBackResult.message);
        }
      }
      putBackIdRef.current = null;

      // Step: Reset sync status in context
      dispatch({ type: 'SET_UNICOMMERCE_SYNC_STATUS', payload: false });

      // Step: If this was a draft, delete it from the backend
      if (state.isDraft && state.draftId) {
        try {
          await WarehouseAPI.deleteDraft(state.draftId);
        } catch (delErr) {
          console.warn('Failed to delete draft during put back:', delErr);
        }
      }

      // Step: Close dialog and clear error
      setError(null);
      setReversalSuccess(true);
    } catch (err: any) {
      console.error('Failed to put back child SKUs:', err);
      setError(`Failed to reverse inventory: ${err.message}`);
    } finally {
      putBackInFlightRef.current = false;
      setPuttingBack(false);
    }
  };

  const handleSaveErrorDraft = async () => {
    try {
      await saveDraft();
      setError(null);
      setSaveSuccess(true);
    } catch (err: any) {
      console.error('Failed to save draft during error:', err);
      setError(`Failed to save draft: ${err.message}`);
    }
  };

  const isLoading = deductMutation.isPending || createBundleMutation.isPending || addBundleMutation.isPending;
  const isSuccess = deductMutation.isSuccess && addBundleMutation.isSuccess;

  const getOperationSteps = () => [
    { label: 'Deduct Child SKUs', completed: deductMutation.isSuccess },
    { label: 'Create Bundle Physically', completed: currentOperation === 'complete' || isSuccess },
    { label: 'Add to Inventory', completed: addBundleMutation.isSuccess },
  ];

  return (
    <Box>
      <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Add />
        Create Bundle
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Final step: Specify bundle storage location and complete creation
      </Typography>

      {/* Bundle Summary */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Bundle Summary
          </Typography>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Creating <strong>{state.quantity}</strong> × <strong>{state.selectedBundleSKU.name}</strong>
          </Typography>

          <Typography variant="subtitle2" gutterBottom>
            Child SKUs to be deducted:
          </Typography>
          <List dense>
            {getAggregatedChildSKUs(state.selectedBundleSKU.childSKUs).map((childSKU: any) => {
              const picking = state.childSKUPicking[childSKU.skuId];
              const totalQuantity = childSKU.requiredQuantity * state.quantity;

              return (
                <ListItem key={childSKU.skuId}>
                  <ListItemText
                    primary={`${childSKU.name} × ${totalQuantity}`}
                    secondary={picking?.shelfPickings.length ?
                      `From shelves: ${picking.shelfPickings.map(sp => `${sp.shelfLocation}(${sp.quantityToPick})`).join(', ')}`
                      : 'N/A'
                    }
                  />
                  <Chip
                    icon={<CheckCircle />}
                    label="Picked"
                    color="success"
                    size="small"
                  />
                </ListItem>
              );
            })}
          </List>
        </CardContent>
      </Card>

      {/* Bundle Storage Locations */}
      <MultipleStorageLocationSelector />

      {/* Legacy Bundle Shelf Location - Keep for backward compatibility */}
      {state.bundleStorageLocations.length === 0 && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="subtitle1" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <LocationOn />
              Bundle Storage Location (Single Location Mode)
            </Typography>
            <Autocomplete
              fullWidth
              options={shelfOptions}
              value={state.bundleShelfLocation || null}
              onChange={handleBundleShelfLocationChange}
              filterOptions={(options, { inputValue }) =>
                options.filter((option) =>
                  option.toLowerCase().includes(inputValue.toLowerCase())
                )
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="Type to search shelves (e.g., 20, DEFAULT, TEST)"
                  label="Shelf Location for Created Bundle"
                  helperText="Search and select the shelf location where the created bundle will be stored"
                  error={!state.bundleShelfLocation.trim()}
                />
              )}
              sx={{ maxWidth: 400 }}
              freeSolo
              clearOnBlur={false}
              selectOnFocus
              handleHomeEndKeys
            />
          </CardContent>
        </Card>
      )}

      {/* Action Button */}
      <Box sx={{ display: 'flex', justifyContent: 'center' }}>
        <Button
          variant="contained"
          size="large"
          onClick={() => setShowConfirmDialog(true)}
          disabled={!allChildSKUsPicked || !hasValidStorageAllocation || isLoading}
          startIcon={isSuccess ? <Done /> : <Add />}
          sx={{ minWidth: 200, py: 1.5 }}
        >
          {isSuccess ? 'Bundle Created Successfully!' : 'Create Bundle'}
        </Button>
      </Box>

      {/* Confirmation Dialog */}
      <Dialog open={showConfirmDialog} onClose={() => !isLoading && setShowConfirmDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Confirm Bundle Creation</DialogTitle>
        <DialogContent>
          {!isLoading && !isSuccess && (
            <Typography gutterBottom>
              Are you sure you want to create {state.quantity} × {state.selectedBundleSKU.name}?
            </Typography>
          )}

          {isLoading && (
            <Box sx={{ textAlign: 'center', py: 2 }}>
              <CircularProgress sx={{ mb: 2 }} />
              <Typography variant="h6" gutterBottom>
                Creating Bundle...
              </Typography>
              <Stepper activeStep={getOperationSteps().findIndex(step => !step.completed)} alternativeLabel>
                {getOperationSteps().map((step, index) => (
                  <Step key={index} completed={step.completed}>
                    <StepLabel>{step.label}</StepLabel>
                  </Step>
                ))}
              </Stepper>
            </Box>
          )}

          {isSuccess && (
            <Box sx={{ textAlign: 'center', py: 2 }}>
              <CheckCircle color="success" sx={{ fontSize: 64, mb: 2 }} />
              <Typography variant="h6" color="success.main">
                Bundle Created Successfully!
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {state.quantity} × {state.selectedBundleSKU.name} added to inventory
              </Typography>
            </Box>
          )}

          {(deductMutation.isError || createBundleMutation.isError || addBundleMutation.isError || error) && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error || 'Bundle creation failed. Please try again or contact support.'}
            </Alert>
          )}

          {reversalSuccess && (
            <Alert severity="success" sx={{ mb: 2 }}>
              Child SKUs have been added back to Unicommerce. Flow completed.
            </Alert>
          )}

          {saveSuccess && (
            <Alert severity="success" sx={{ mb: 2 }}>
              Draft saved successfully! You can resume this bundle later.
            </Alert>
          )}
        </DialogContent>
        <DialogActions sx={{ pb: 3, px: 3 }}>
          {reversalSuccess || saveSuccess ? (
            <Button
              variant="contained"
              onClick={onComplete}
              fullWidth
            >
              Continue to Dashboard
            </Button>
          ) : (
            <>
              {!isLoading && !isSuccess && (
                <>
                  {error ? (
                    <>
                      <Button
                        onClick={handlePutBackChild}
                        disabled={puttingBack}
                        color="warning"
                        sx={{ mr: 'auto' }}
                      >
                        {puttingBack ? 'Putting Back...' : 'Put Back Child'}
                      </Button>
                      <Button
                        onClick={handleSaveErrorDraft}
                        disabled={state.savingDraft}
                      >
                        {state.savingDraft ? 'Saving...' : 'Save Draft'}
                      </Button>
                      <Button
                        variant="contained"
                        onClick={handleCreateBundle}
                        disabled={!!currentOperation}
                      >
                        Retry
                      </Button>
                    </>
                  ) : (
                    <>
                      <Button onClick={() => setShowConfirmDialog(false)}>Cancel</Button>
                      <Button
                        variant="contained"
                        onClick={handleCreateBundle}
                        disabled={!!currentOperation}
                      >
                        {currentOperation ? 'Processing...' : 'Confirm & Create'}
                      </Button>
                    </>
                  )}
                </>
              )}
            </>
          )}
          {isSuccess && (
            <Button variant="contained" onClick={() => setShowConfirmDialog(false)}>
              Close
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Success Modal */}
      <Dialog
        open={showSuccessModal}
        onClose={() => {
          setShowSuccessModal(false);
          onComplete();
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          backgroundColor: '#4caf50',
          color: 'white'
        }}>
          <CheckCircle />
          Bundle Created Successfully!
        </DialogTitle>
        <DialogContent sx={{ pt: 3, mt: 2 }}>
          <Typography variant="h6" gutterBottom sx={{ mt: 1 }}>
            🎉 Your bundle has been created and added to inventory!
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
            The bundle has been successfully created and the inventory has been updated on Unicommerce.
          </Typography>

          <Box sx={{
            mt: 2,
            p: 2,
            backgroundColor: 'rgba(76, 175, 80, 0.1)',
            border: '1px solid rgba(76, 175, 80, 0.3)',
            borderRadius: 1
          }}>
            <Typography variant="body2" sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              color: 'success.main'
            }}>
              <Done sx={{ color: 'success.main' }} />
              <strong>Bundle Details:</strong>
            </Typography>
            <Typography variant="body2" sx={{ mt: 1, ml: 3 }}>
              • Bundle: {state.selectedBundleSKU?.name}
            </Typography>
            <Typography variant="body2" sx={{ ml: 3 }}>
              • Quantity: {state.quantity} units
            </Typography>
            <Typography variant="body2" sx={{ ml: 3 }}>
              • Location: {state.bundleShelfLocation}
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setShowSuccessModal(false);
              onComplete();
            }}
            variant="contained"
            color="success"
            size="large"
          >
            Continue
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default BundleCreator;